// Client side prediction: the local player moves immediately; every server
// snapshot is compared with what we predicted for the same command. Movement
// is deterministic, so they normally agree bit for bit; when they do not
// (a rejected command, a server-side action) the client takes the server
// state and re-simulates the commands the server has not processed yet.

import type { MoverSnapshot } from '../shared/game/movers';
import type { PlayerSession } from '../shared/game/session';
import type { PlayerState, UserCmd } from '../shared/physics/pmove';

interface Entry {
  cmd: UserCmd;
  /** Predicted state after the command. */
  ps: PlayerState;
  /** The player's movers after the command (restored when re-simulating). */
  movers: MoverSnapshot;
}

const MAX_HISTORY = 1000;

function sameState(a: PlayerState, b: PlayerState): boolean {
  for (let i = 0; i < 3; i++) {
    if (a.origin[i] !== b.origin[i] || a.velocity[i] !== b.velocity[i] || a.deltaAngles[i] !== b.deltaAngles[i]) return false;
  }
  return (
    a.pmFlags === b.pmFlags &&
    a.pmTime === b.pmTime &&
    a.groundEntityNum === b.groundEntityNum &&
    a.commandTime === b.commandTime &&
    a.pmType === b.pmType &&
    a.jumpTime === b.jumpTime &&
    a.eFlags === b.eFlags
  );
}

export class Prediction {
  private history: Entry[] = [];
  corrections = 0;

  /** Call after the local session ran `cmd`. */
  record(cmd: UserCmd, session: PlayerSession): void {
    this.history.push({ cmd, ps: structuredClone(session.ps), movers: session.movers.snapshot() });
    if (this.history.length > MAX_HISTORY) this.history.shift();
  }

  /** Forgets everything predicted (the player starts over, e.g. after spectating). */
  reset(): void {
    this.history = [];
  }

  /** A local action (save/load/...) changed the state after the last command. */
  afterAction(session: PlayerSession): void {
    const last = this.history[this.history.length - 1];
    if (last) last.ps = structuredClone(session.ps);
  }

  /**
   * Applies a server snapshot of our own player.
   * @returns true when the prediction had to be corrected
   */
  reconcile(session: PlayerSession, server: { ps: PlayerState; health: number; lastCmdTime: number; moverTime?: number | null; movers?: MoverSnapshot }): boolean {
    // commands up to lastCmdTime are acknowledged
    let idx = this.history.findIndex((e) => e.cmd.serverTime >= server.lastCmdTime);
    if (idx < 0) idx = this.history.length;
    const acked = this.history[idx];
    const exact = acked && acked.cmd.serverTime === server.lastCmdTime;
    const pending = this.history.slice(exact ? idx + 1 : idx);
    this.history = exact ? this.history.slice(idx) : pending;

    // other players changed the movers lately: the server's movers win (they have the shared events in place)
    const sameMovers = server.movers ? exact && JSON.stringify(acked.movers.states) === JSON.stringify(server.movers.states) && acked.movers.time === server.movers.time : true;
    if (exact && sameMovers && sameState(acked.ps, server.ps) && (server.moverTime === undefined || acked.movers.time === server.moverTime)) return false;
    if (!exact && !pending.length) return false; // nothing predicted yet

    // take the server's state and replay the commands it has not seen yet; movers
    // are deterministic per player, so ours as of the acknowledged command are the server's
    this.corrections++;
    session.ps = structuredClone(server.ps);
    session.health = server.health;
    if (server.movers) session.movers.restore(server.movers);
    else if (exact) session.movers.restore(acked.movers);
    session.muted = true;
    try {
      for (const e of pending) {
        session.tick(e.cmd);
        e.ps = structuredClone(session.ps);
        e.movers = session.movers.snapshot();
      }
    } finally {
      session.muted = false;
    }
    return true;
  }
}
